/** Application settings + periode helpers. */

import { COL } from '../../collections';
import { useLiveQuery } from '../hooks';

export interface AppSettings {
  loading: boolean;
  /** Readable label, e.g. "Ganjil 2025/2026" — derived from tahunAjaran + semester. */
  periode: string;
  /** Structured tahun ajaran, e.g. "2025/2026". */
  tahunAjaran: string;
  /** Ganjil / Genap. */
  semester: string;
  /** Dekan (fakultas-level signing official) — for dekan-assigned documents. */
  dekanNama: string;
  dekanNip: string;
  turnitinThreshold: number;
  /**
   * Ambang kemiripan untuk SEMINAR PROPOSAL. `0` berarti turnitin tidak
   * wajib di sempro (sesuai Pedoman Akademik FKIP Untan 2023/2024 — uji
   * kemiripan hanya dipersyaratkan sebelum ujian skripsi).
   */
  turnitinThresholdSempro: number;
  /** FKIP Untan: minimum bimbingan proposal before seminar proposal. */
  minimumBimbinganSempro: number;
  /** FKIP Untan: minimum bimbingan per pembimbing before sidang. */
  minimumBimbinganSidangPerPembimbing: number;
  /** FKIP Untan: minimum SKS (min C) before seminar proposal. */
  sksMinimumSempro: number;
  /** FKIP Untan: TUTEP/TOEFL requirement. */
  tutepRequired: boolean;
  tutepThreshold: number;
  /** FKIP Untan: publikasi artikel wajib untuk lulus. */
  wajibArtikel: boolean;
}

/** Format a struktur tahun ajaran + semester into a readable periode label. */
export function formatPeriode(tahunAjaran: string, semester: string): string {
  const ta = tahunAjaran.trim();
  const sm = semester.trim();
  if (!ta) return sm || '—';
  return sm ? `${sm} ${ta}` : ta;
}

/** Raw settings document (doc id `app-settings`); all fields optional. */
export interface SettingsDoc {
  tahunAkademik?: string;
  tahunAjaran?: string;
  semester?: string;
  dekanNama?: string;
  dekanNip?: string;
  turnitinThreshold?: number;
  /**
   * Ambang kemiripan untuk sempro; `0`/tidak diset = turnitin tidak wajib
   * di sempro (default mengikuti Pedoman Akademik FKIP Untan).
   */
  turnitinThresholdSempro?: number;
  /** FKIP Untan: minimum bimbingan proposal before seminar proposal. */
  minimumBimbinganSempro?: number;
  /** FKIP Untan: minimum bimbingan per pembimbing before sidang. */
  minimumBimbinganSidangPerPembimbing?: number;
  /** FKIP Untan: minimum SKS (min C) before seminar proposal. */
  sksMinimumSempro?: number;
  /** FKIP Untan: TUTEP/TOEFL requirement. */
  tutepRequired?: boolean;
  tutepThreshold?: number;
  /** FKIP Untan: publikasi artikel wajib untuk lulus. */
  wajibArtikel?: boolean;
}

/** Live, typed application settings (doc id `app-settings`). */
export function useAppSettings(): AppSettings {
  const { data, loading } = useLiveQuery<SettingsDoc>(COL.settings);
  const app = data.find((s) => s.id === 'app-settings');
  const tahunAjaran = app?.tahunAkademik ?? app?.tahunAjaran ?? '';
  const semester = app?.semester ?? '';
  const periode = formatPeriode(tahunAjaran, semester);
  return {
    loading,
    periode,
    tahunAjaran,
    semester,
    dekanNama: app?.dekanNama ?? '',
    dekanNip: app?.dekanNip ?? '',
    turnitinThreshold: Number(app?.turnitinThreshold ?? 25),
    turnitinThresholdSempro: Number(app?.turnitinThresholdSempro ?? 0),
    minimumBimbinganSempro: Number(app?.minimumBimbinganSempro ?? 4),
    minimumBimbinganSidangPerPembimbing: Number(app?.minimumBimbinganSidangPerPembimbing ?? 6),
    sksMinimumSempro: Number(app?.sksMinimumSempro ?? 90),
    tutepRequired: app?.tutepRequired ?? true,
    tutepThreshold: Number(app?.tutepThreshold ?? 450),
    wajibArtikel: app?.wajibArtikel ?? true,
  };
}
